import { assoc, compose, pathOr, pick, propOr } from 'ramda'

import { formatApiDate } from '../../../../utils/helpers/date'
import moment from 'moment'

// Mirrors the equivalent so_item branch in
// src/modules/triggers/editV2/_requests/cleanRequest.js (cleanServiceOrderUserOrGroup,
// insert_service_orders/schedule_insert_service_orders case) so routines save/load the
// "responsible" field with the same shape that module already round-trips correctly:
// keep `user_or_group` itself (the field the form reads back on reopen) alongside the
// scalar `user_id`/`group_id`/`user_group_ids` the backend expects.
const cleanEmptyValue = value => {
  if (!value) return null

  return value
}

const cleanServiceOrderUserOrGroup = so_item => {
  if (Array.isArray(so_item?.user_or_group)) {
    return compose(
      item =>
        assoc(
          'user_or_group',
          item.user_or_group.map(group => ({
            id: group.id,
            name: group.name,
            __typename: group.__typename
          })),
          item
        ),
      item =>
        assoc(
          'user_group_ids',
          item.user_or_group.map(group => group.id),
          item
        ),
      item => assoc('user_id', null, item),
      item => assoc('group_id', null, item)
    )(so_item)
  }

  if (so_item?.user_or_group?.__typename === 'User') {
    return compose(
      item =>
        assoc(
          'user_or_group',
          {
            id: cleanEmptyValue(item.user_or_group.contract?.id || item.user_or_group.id),
            name: item.user_or_group.name,
            __typename: item.user_or_group.__typename,
            user_id: cleanEmptyValue(item.user_or_group.user_id || item.user_or_group.id)
          },
          item
        ),
      item => assoc('user_id', cleanEmptyValue(item.user_or_group.user_id || item.user_or_group.id), item),
      item => assoc('group_id', null, item)
    )(so_item)
  }

  if (so_item?.user_or_group?.__typename === 'UserGroup') {
    return compose(
      item =>
        assoc(
          'user_or_group',
          {
            id: item.user_or_group.contract?.id || item.user_or_group.id,
            name: item.user_or_group.name,
            __typename: item.user_or_group.__typename,
            group_id: item.user_or_group.user_id || item.user_or_group.id
          },
          item
        ),
      item => assoc('user_id', null, item),
      item => assoc('group_id', item.user_or_group.user_id || item.user_or_group.id, item)
    )(so_item)
  }

  return compose(
    item => assoc('user_or_group', null, item),
    item => assoc('user_id', null, item),
    item => assoc('group_id', null, item)
  )(so_item)
}

const cleanServiceOrderPayload = payload => {
  return compose(
    pick([
      'user_id',
      'checklist_id',
      'place_id',
      'asset_id',
      'scheduling',
      'priority_id',
      'address',
      'description',
      'parent_id',
      'parent_dependent',
      'documents',
      'group_id',
      'user_group_ids',
      'user_or_group',
      'type',
      'non_compliance_id',
      'form_reply_id',
      'bond',
      'metadata',
      'name'
    ]),
    item => assoc('scheduling', formatApiDate(item.scheduling), item),
    item => assoc('place_id', pathOr(null, ['place', 'id'], item), item),
    item => assoc('asset_id', pathOr(null, ['asset', 'id'], item), item),
    cleanServiceOrderUserOrGroup,
    item => assoc('checklist_id', pathOr(null, ['checklist', 'id'], item), item)
  )(payload)
}

const cleanServiceOrdersPayload = payload => {
  return payload.map(item => cleanServiceOrderPayload(item))
}

const cleanPayload = trigger =>
  compose(
    JSON.stringify, //
    payload => cleanServiceOrdersPayload(payload),
    propOr({}, 'payload')
  )(trigger)

const genFrequency = item => {
  switch (item.frequencyType.id) {
    case 'day':
      return {
        frequency: '0 1 * * *',
        frequencyIndex: item.frequencyIndex
      }
    case 'week':
      return {
        frequency: `0 1 * * ${item.weekday}`,
        frequencyIndex: item.frequencyIndex
      }
    case 'year':
      return {
        frequency: '0 1 1 1 *',
        frequencyIndex: item.frequencyIndex
      }
    default:
      return {
        frequency: '* * * * *',
        frequencyIndex: null
      }
  }
}

export const cleanRequest = item =>
  compose(
    pick([
      'action',
      'entity',
      'event',
      'type',
      'payload',
      'conditions',
      'name',
      'folderId',
      'id',
      'frequency',
      'frequencyIndex',
      'integrationId',
      'initialDate',
      'until',
      'isRoutine'
    ]),
    assoc('entity', 'service_order'),
    assoc('type', 'recurrent'),
    assoc('event', 'routine'),
    assoc('isRoutine', true),
    assoc('action', 'create_bulk_service_orders_with_parent'),
    item => assoc('frequencyIndex', genFrequency(item).frequencyIndex, item),
    item => assoc('frequency', genFrequency(item).frequency, item),
    item => assoc('folderId', pathOr(null, ['folder', 'id'], item), item),
    item =>
      assoc(
        'initialDate',
        formatApiDate(moment(item.initialDate).set({ hour: 0, minute: 0, second: 0, millisecond: 0 })),
        item
      ),
    item =>
      assoc(
        'until',
        item.until === 'never'
          ? null
          : formatApiDate(moment(item.until).set({ hour: 0, minute: 0, second: 0, millisecond: 0 })),
        item
      ),
    assoc('conditions', JSON.stringify([])),
    assoc('payload', cleanPayload(item))
  )(item)
